import React, { useState } from 'react';
import { isWeekend, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { formatHoursForDay } from '@/lib/heures-utils';
import { X, Users, MapPin, Calendar, Clock, UsersRound } from 'lucide-react';
import { ChantierIcon } from '@/components/chantiers/ChantierIcon';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';
import type { CalendarDay, CalendarAffectation } from '@/types/calendar';
import { PlanningBadgeGrouped } from '@/components/planning/PlanningBadgeGrouped';

// Type pour les affectations groupées par chantier
interface GroupedChantier {
  chantier: CalendarAffectation['chantier'];
  memberCount: number;
  affectations: CalendarAffectation[];
}

// Calcule le nombre de jours ouvrés (lun-ven) entre deux dates
function countBusinessDays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    if (!isWeekend(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

/**
 * Vérifie si une affectation doit être affichée pour un jour donné.
 * Utilise jours_details (JSONB) si disponible pour une précision exacte,
 * sinon utilise la logique classique (plage de dates + jours ouvrés).
 */
function shouldShowOnDay(aff: CalendarAffectation, day: Date): boolean {
  const dateStr = format(day, 'yyyy-MM-dd');

  // Normaliser les dates pour ignorer les heures (comparaison par chaîne YYYY-MM-DD)
  const dayStr = dateStr;
  const debutStr = aff.date_debut.split('T')[0]; // Prendre uniquement la partie date
  const finStr = aff.date_fin.split('T')[0];

  // Vérifier d'abord si le jour est dans la plage de dates (comparaison par chaîne)
  if (dayStr < debutStr || dayStr > finStr) {
    return false;
  }

  // Si jours_details est disponible, utiliser cette info précise
  if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
    // Chercher si ce jour est dans jours_details
    return aff.jours_details.some(jd => jd.date === dateStr);
  }

  // Fallback: logique classique pour les affectations sans jours_details
  // Pour les weekends, vérifier si jours_travailles > jours ouvrés
  if (isWeekend(day)) {
    const debut = new Date(debutStr);
    const fin = new Date(finStr);
    const joursOuvresTheoriques = countBusinessDays(debut, fin);
    const joursTravailles = aff.jours_travailles || 0;
    return joursTravailles > joursOuvresTheoriques;
  }

  // Jour de semaine dans la plage = afficher
  return true;
}

// Fonction utilitaire pour regrouper les affectations par chantier (avec filtre par jour)
function groupAffectationsByChantier(affectations: CalendarAffectation[], day: Date): GroupedChantier[] {
  // Filtrer les affectations pour ce jour spécifique
  const filteredAffectations = affectations.filter(aff => shouldShowOnDay(aff, day));

  const byChantier: Record<string, CalendarAffectation[]> = {};

  filteredAffectations.forEach(aff => {
    const chantierId = aff.chantier_id;
    if (!byChantier[chantierId]) {
      byChantier[chantierId] = [];
    }
    byChantier[chantierId].push(aff);
  });

  return Object.values(byChantier).map(chantierAffectations => ({
    chantier: chantierAffectations[0]?.chantier,
    memberCount: chantierAffectations.length,
    affectations: chantierAffectations,
  }));
}

interface CalendarGridLargeProps {
  days: CalendarDay[];
  weekDays: string[];
  onDayClick: (date: Date) => void;
}

/**
 * Grille de calendrier spacieuse avec cellules de 100px minimum
 * Affiche les badges chantier/équipe pour chaque affectation
 * La grande modal de détail s'affiche au hover sur un badge chantier
 */
const CalendarGridLarge = ({ days, weekDays, onDayClick }: CalendarGridLargeProps) => {
  // État pour la modal de détail au hover sur un badge
  const [hoveredChantier, setHoveredChantier] = useState<{ day: CalendarDay; grouped: GroupedChantier } | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Gestion du hover avec délai sur le badge chantier
  const handleBadgeMouseEnter = (day: CalendarDay, grouped: GroupedChantier) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    // Délai de 300ms avant d'afficher la modal
    const timeout = setTimeout(() => {
      setHoveredChantier({ day, grouped });
    }, 300);
    setHoverTimeout(timeout);
  };

  const handleBadgeMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const closeHoverModal = () => {
    setHoveredChantier(null);
  };

  return (
    <div className="bg-card/30 rounded-2xl border border-border/50 overflow-hidden">
      {/* ═══ EN-TÊTE DES JOURS DE LA SEMAINE ═══ */}
      <div className="grid grid-cols-7 bg-muted/30">
        {weekDays.map((day, idx) => {
          // Samedi = index 5, Dimanche = index 6 (semaine commence lundi)
          const isWeekendDay = idx >= 5;
          const isLastCol = idx === 6;
          return (
            <div
              key={day}
              className={cn(
                "text-center text-xs font-semibold uppercase tracking-wider py-3",
                "border-b border-border",
                !isLastCol && "border-r border-border",
                isWeekendDay ? "text-amber-500 bg-amber-500/10" : "text-muted-foreground"
              )}
            >
              {day}
            </div>
          );
        })}
      </div>

      {/* ═══ GRILLE DES JOURS ═══ */}
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const isDayWeekend = isWeekend(day.date);
          // Regrouper les affectations par chantier (avec filtre par jour précis via jours_details)
          const groupedChantiers = groupAffectationsByChantier(day.affectations, day.date);
          // Limiter à 3 chantiers visibles par jour
          const displayedChantiers = groupedChantiers.slice(0, 3);
          const hiddenCount = groupedChantiers.length - 3;
          const isLastCol = index % 7 === 6;
          const isLastRow = index >= days.length - 7;

          return (
            <button
              key={index}
              onClick={() => onDayClick(day.date)}
              className={cn(
                // Dimensions spacieuses
                'min-h-[100px] p-2',
                'flex flex-col items-start justify-start gap-1',
                // Transitions
                'transition-all duration-150',
                // Bordures pour délimiter les cellules
                !isLastCol && 'border-r border-border',
                !isLastRow && 'border-b border-border',
                // Weekend : fond ambré léger
                isDayWeekend && 'bg-amber-500/5',
                // Jours hors du mois
                !day.isCurrentMonth && 'opacity-30',
                // Jour sélectionné
                day.isSelected && [
                  'ring-2 ring-primary ring-inset',
                  'bg-primary/10',
                ],
                // Aujourd'hui (si pas sélectionné)
                day.isToday && !day.isSelected && [
                  'bg-primary/5',
                ]
              )}
            >
              {/* Numéro du jour */}
              <span
                className={cn(
                  'text-lg font-bold leading-none mb-1',
                  day.isToday && 'text-primary',
                  day.isSelected && 'text-primary',
                  isDayWeekend && !day.isToday && !day.isSelected && 'text-amber-500',
                  !day.isCurrentMonth && 'text-muted-foreground/50'
                )}
              >
                {day.dayOfMonth}
              </span>

              {/* Badges des chantiers (groupés) - hover sur chaque badge */}
              <div className="flex flex-col gap-0.5 w-full">
                {displayedChantiers.map((grouped) => (
                  <div
                    key={grouped.chantier?.id || grouped.affectations[0]?.id}
                    onMouseEnter={() => handleBadgeMouseEnter(day, grouped)}
                    onMouseLeave={handleBadgeMouseLeave}
                  >
                    <PlanningBadgeGrouped
                      chantier={grouped.chantier}
                      memberCount={grouped.memberCount}
                      affectations={grouped.affectations}
                      compact={groupedChantiers.length > 1}
                    />
                  </div>
                ))}
                {hiddenCount > 0 && (
                  <span className="text-[10px] text-muted-foreground font-medium text-center mt-0.5">
                    +{hiddenCount} autre{hiddenCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MODAL DE DÉTAIL AU HOVER SUR UN BADGE CHANTIER
          ═══════════════════════════════════════════════════════════════ */}
      {hoveredChantier && (
        <>
          {/* Overlay semi-transparent */}
          <div
            className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm"
            onClick={closeHoverModal}
          />

          {/* Modal centrale */}
          <div
            className={cn(
              'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70]',
              'w-[90vw] max-w-[600px] max-h-[80vh]',
              'bg-card border border-border rounded-2xl shadow-2xl',
              'flex flex-col overflow-hidden',
              'animate-in fade-in-0 zoom-in-95 duration-200'
            )}
            onMouseLeave={closeHoverModal}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "p-2 rounded-lg",
                  isWeekend(hoveredChantier.day.date) ? "bg-amber-500/20" : "bg-primary/10"
                )}>
                  <Calendar className={cn(
                    "w-5 h-5",
                    isWeekend(hoveredChantier.day.date) ? "text-amber-500" : "text-primary"
                  )} />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">
                    {format(hoveredChantier.day.date, 'EEEE d MMMM yyyy', { locale: fr })}
                  </h3>
                  <p className="text-sm text-foreground/80 font-medium">
                    {hoveredChantier.grouped.memberCount} ouvrier{hoveredChantier.grouped.memberCount > 1 ? 's' : ''} affecté{hoveredChantier.grouped.memberCount > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={closeHoverModal}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
                {/* Info Chantier */}
                <div className="flex items-start gap-3 mb-3">
                  {/* Pictogramme */}
                  <ChantierIcon
                    pictogramme={hoveredChantier.grouped.chantier?.pictogramme || 'Home'}
                    couleur={hoveredChantier.grouped.chantier?.couleur || '#6366f1'}
                    size="lg"
                    showBackground={true}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-foreground text-lg truncate">
                        {hoveredChantier.grouped.chantier?.nom_chantier || 'Chantier inconnu'}
                      </h4>
                      {/* Badge Retard si au moins une affectation est un retard */}
                      {hoveredChantier.grouped.affectations.some(aff => aff.notes?.startsWith('Retard chantier')) && (
                        <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-500 border border-orange-500/30">
                          Retard
                        </span>
                      )}
                    </div>
                    {hoveredChantier.grouped.chantier?.client && (
                      <p className="text-sm text-foreground/80 font-medium">
                        Client : {hoveredChantier.grouped.chantier.client}
                      </p>
                    )}
                    {hoveredChantier.grouped.chantier?.adresse && (
                      <p className="text-xs text-foreground/70 font-medium flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {hoveredChantier.grouped.chantier.adresse}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-1 rounded-full text-sm font-medium">
                    <Users className="w-4 h-4" />
                    {hoveredChantier.grouped.memberCount}
                  </div>
                </div>

                {/* Équipes affectées */}
                {(() => {
                  // Extraire les équipes uniques pour ce chantier
                  const equipesMap = new Map<string, { id: string; nom: string; pictogramme?: string; couleur?: string }>();
                  hoveredChantier.grouped.affectations.forEach(aff => {
                    if (aff.equipe?.id && !equipesMap.has(aff.equipe.id)) {
                      equipesMap.set(aff.equipe.id, {
                        id: aff.equipe.id,
                        nom: aff.equipe.nom,
                        pictogramme: aff.equipe.pictogramme,
                        couleur: aff.equipe.couleur,
                      });
                    }
                  });
                  const uniqueEquipes = Array.from(equipesMap.values());

                  if (uniqueEquipes.length === 0) return null;

                  return (
                    <div className="border-t border-border/50 pt-3 mb-3">
                      <p className="text-xs font-semibold text-[#F59E0B] uppercase tracking-wider mb-2 flex items-center gap-1">
                        <UsersRound className="w-3 h-3" />
                        Équipe{uniqueEquipes.length > 1 ? 's' : ''} affectée{uniqueEquipes.length > 1 ? 's' : ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {uniqueEquipes.map((equipe) => (
                          <div
                            key={equipe.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-card border border-border/30"
                          >
                            <EquipeIcon
                              pictogramme={equipe.pictogramme || 'Wrench'}
                              couleur={equipe.couleur || '#f97316'}
                              size="sm"
                              showBackground={true}
                            />
                            <span className="text-sm font-medium text-foreground">
                              {equipe.nom}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Liste des ouvriers */}
                <div className="border-t border-border/50 pt-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Ouvriers affectés
                  </p>
                  <div className="space-y-2">
                    {hoveredChantier.grouped.affectations.map((aff) => (
                      <div
                        key={aff.id}
                        className="flex items-center justify-between bg-card rounded-lg p-2 border border-border/30"
                      >
                        <div className="flex items-center gap-2">
                          {/* Picto équipe du membre */}
                          <EquipeIcon
                            pictogramme={aff.equipe?.pictogramme || 'Wrench'}
                            couleur={aff.equipe?.couleur || '#f97316'}
                            size="sm"
                            showBackground={true}
                          />
                          <div>
                            <p className="font-semibold text-foreground text-sm">
                              {aff.membre?.prenom} {aff.membre?.nom}
                            </p>
                            <p className="text-xs text-foreground/70 font-medium">
                              {aff.membre?.specialite || aff.membre?.poste || 'Ouvrier'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-xs text-foreground/80 font-medium">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatHoursForDay(aff, hoveredChantier.day.date)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-border bg-muted/20 text-center">
              <p className="text-xs text-muted-foreground">
                Cliquez pour fermer • Survolez un autre chantier pour changer
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarGridLarge;
